
import Image from 'next/image';
import Link from 'next/link';
import { Bed, Bath, MapPin } from 'lucide-react';
import { Property } from '@/types/property';
import { getPrimaryPropertyImageUrl, shouldUnoptimizedPropertyImage } from '@/lib/property-image';
import { getAmenityIcon } from '@/lib/amenity-icons';
import { getPropertyHref } from '@/lib/property-url';

export function getPropertyImage(property: Property): string | null {
    return getPrimaryPropertyImageUrl(property);
}

export function formatPrice(price: number): string {
    return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(price);
}

interface PropertyCardProps {
    property: Property;
}

export function PropertyCard({ property }: PropertyCardProps) {
    const normalizedRentType = (property.rent_type || 'monthly').toString().toLowerCase();
    const rentSuffix = normalizedRentType === 'annual' ? '/yr' : '/mo';
    const propertyImage = getPropertyImage(property);

    return (
        <Link
            href={getPropertyHref(property)}
            className="flex flex-col group cursor-pointer"
            title={property.title}
        >
            {/* Image Container */}
            <div className="relative aspect-[4/3] w-full rounded-[2rem] overflow-hidden mb-5 bg-muted flex items-center justify-center">
                {propertyImage ? (
                    <Image
                        src={propertyImage}
                        alt={property.title}
                        title={property.title}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                        unoptimized={shouldUnoptimizedPropertyImage(propertyImage)}
                    />
                ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <MapPin className="w-8 h-8 opacity-20" />
                        <span className="text-sm font-medium">No images available</span>
                    </div>
                )}
            </div>

            {/* Details */}
            <div className="flex flex-col gap-2">
                {/* Icons Row */}
                <div className="flex items-center gap-6 text-muted-foreground text-sm mb-1">
                    <div className="flex items-center gap-2">
                        <Bed className="w-5 h-5 stroke-1" />
                        <span>{property.bedrooms} Bedrooms</span>
                    </div>
                    {property.bathrooms > 0 && (
                        <div className="flex items-center gap-2">
                            <Bath className="w-5 h-5 stroke-1" />
                            <span>{property.bathrooms} Bathroom</span>
                        </div>
                    )}
                </div>

                {property.amenities && property.amenities.length > 0 && (
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                        {property.amenities.slice(0, 4).map((amenity) => {
                            const AmenityIcon = getAmenityIcon(amenity);
                            return (
                                <div key={amenity} className="flex items-center gap-1 text-muted-foreground/70 text-xs" title={amenity}>
                                    <AmenityIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
                                </div>
                            );
                        })}
                        {property.amenities.length > 4 && (
                            <span className="text-xs text-muted-foreground">+{property.amenities.length - 4}</span>
                        )}
                    </div>
                )}

                <h3 className="text-2xl font-medium text-foreground tracking-tight line-clamp-1">
                    {property.title}
                </h3>

                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <span className="font-semibold text-foreground text-lg">
                        {formatPrice(property.price)}
                        <span className="ml-1 text-sm font-medium text-muted-foreground">{rentSuffix}</span>
                    </span>
                    <span className="w-1 h-1 bg-muted-foreground/40 rounded-full"></span>
                    <span className="flex items-center gap-1 line-clamp-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {property.location_data?.short_name || property.location}
                    </span>
                </div>
            </div>
        </Link>
    );
}
